import React, { Component } from 'react';
import styled from 'styled-components';

import { colors } from 'bench-style-constants';
import { NullState } from 'null-state';

import HistoryReview from './HistoryReview';
import Header from './Header';
import Title from './Title';
import ToggleButton from './ToggleButton';
import MonthRangePopover from './MonthRangePopover';

const Wrapper = styled.div`
  min-height: 100vh;
  display: grid;
  grid-template-rows: auto 1fr;
`;

const HeaderLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  grid-column-gap: 12px;
  align-items: center;
  flex: 1;
`;

const ViewByWrapper = styled.span`
  font-size: 12px;
  color: ${colors.smoke};
  padding-right: 4px;
`;

const HistoryReviewList = styled.div`
  padding: 12px 32px;
`;

const NullStateWrapper = styled.div`
  margin: 32px auto;
  width: 300px;
`;

const HistoryGroup = styled.div`
  padding: 12px 0;
  border-bottom: 1px solid ${colors.shell};

  &:last-child {
    border: none;
  }
`;

const historyTabs = {
  MONTHLY: 'monthly',
  REVIEW_PERIOD: 'review period',
};

const HistoryPeriod = data =>
  data.map(({ id, notes, status, fromMonth, toMonth, submittedAt, submitterId }) => (
    <HistoryGroup key={id}>
      <HistoryReview
        notes={notes}
        status={status}
        fromDate={fromMonth}
        toDate={toMonth}
        submittedAt={submittedAt}
        submitterId={submitterId}
      />
    </HistoryGroup>
  ));

const HistoryMonthly = data =>
  data.map(({ month, reviews }) => (
    <HistoryGroup key={month}>
      {reviews.map(({ id, notes, status, submittedAt, submitterId }, index) => (
        <HistoryReview
          key={`${month}_${id}`}
          notes={notes}
          status={status}
          fromDate={month}
          toDate={month}
          hideDate={index !== 0}
          submittedAt={submittedAt}
          submitterId={submitterId}
        />
      ))}
    </HistoryGroup>
  ));

type HistoryProps = {
  periodData: Array<Record<string, unknown>>;
  monthlyData: Array<Record<string, unknown>>;
  error?: string;
  onStartReview: (restoreLastChecks: boolean, startDate: string, endDate: string) => void;
};

type HistoryState = {
  activeTab: string;
};

class History extends Component<HistoryProps, HistoryState> {
  static defaultProps = {
    data: [],
    error: null,
  };

  constructor(props: HistoryProps) {
    super(props);

    this.state = {
      activeTab: historyTabs.MONTHLY,
    };
  }

  changeTab = (tab: string) => {
    this.setState({
      activeTab: tab,
    });
  };

  render() {
    const { periodData, monthlyData, onStartReview, error } = this.props;

    const { activeTab } = this.state;

    return (
      <Wrapper>
        <Header>
          <HeaderLayout>
            <div>
              <Title aria-label="Review History Page">Review History</Title>
              <ViewByWrapper>View By:</ViewByWrapper>
              <ToggleButton
                active={activeTab === historyTabs.MONTHLY}
                onClick={() => this.changeTab(historyTabs.MONTHLY)}
              >
                Month
              </ToggleButton>
              <ToggleButton
                active={activeTab === historyTabs.REVIEW_PERIOD}
                onClick={() => this.changeTab(historyTabs.REVIEW_PERIOD)}
              >
                Review Period
              </ToggleButton>
            </div>
            <MonthRangePopover onSubmit={onStartReview} />
          </HeaderLayout>
        </Header>
        {error && (
          <NullStateWrapper>
            <NullState title="Error" subtitle={error} />
          </NullStateWrapper>
        )}
        {!error && monthlyData.length === 0 ? (
          <NullStateWrapper>
            <NullState subtitle="This client does not have any reviews yet. Click the button up top to get started." />
          </NullStateWrapper>
        ) : (
          <HistoryReviewList>
            {activeTab === historyTabs.MONTHLY && HistoryMonthly(monthlyData)}
            {activeTab === historyTabs.REVIEW_PERIOD && HistoryPeriod(periodData)}
          </HistoryReviewList>
        )}
      </Wrapper>
    );
  }
}

export default History;
